// AuthLayout.js
// Layout de pantalla dividida (estilo Instagram desktop) para Login/Register.
// En mobile no cambia nada: el panel izquierdo se oculta y el formulario
// ocupa toda la pantalla como antes.
import React from 'react';
import { Box, Typography, Stack, Paper, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
  Storefront as StorefrontIcon,
  Event as EventIcon,
  AccountBalance as AccountBalanceIcon,
} from '../icons';
import { Explore as ExploreIcon } from '../icons';
import { T } from './Translate';
import { brandColors } from '../theme';
import PublicFooter from '../components/PublicFooter';

const FloatingBadge = ({ icon, label, sx }) => (
  <Paper
    elevation={6}
    sx={{
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 1,
      borderRadius: 3,
      bgcolor: '#ffffff',
      color: '#15171f',
      boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
      ...sx,
    }}
  >
    {icon}
    <Typography variant="caption" fontWeight={600} noWrap sx={{ color: '#15171f' }}>
      {label}
    </Typography>
  </Paper>
);

const feedRow = (color) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.5 }}>
    <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: alpha(color, 0.9), flexShrink: 0 }} />
    <Stack spacing={0.6} sx={{ flex: 1 }}>
      <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.22)', width: '70%' }} />
      <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.12)', width: '45%' }} />
    </Stack>
  </Stack>
);

const AuthShowcase = () => (
  <Box
    sx={{
      display: { xs: 'none', md: 'flex' },
      flex: '1 1 58%',
      position: 'relative',
      alignItems: 'center',
      justifyContent: 'center',
      px: 6,
      py: 6,
      background: `radial-gradient(circle at 20% 20%, ${alpha('#a0b4ff', 0.18)}, transparent 45%), linear-gradient(160deg, ${brandColors.primary} 0%, #05082b 100%)`,
      color: '#ffffff',
    }}
  >
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0 }}>
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          right: -80,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: alpha(brandColors.accent, 0.15),
          filter: 'blur(10px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -100,
          left: -60,
          width: 280,
          height: 280,
          borderRadius: '50%',
          background: alpha('#ffffff', 0.06),
        }}
      />
    </Box>

    <Stack spacing={5} alignItems="center" sx={{ position: 'relative', zIndex: 1, maxWidth: 420 }}>
      <Box sx={{ position: 'relative', width: 200, mx: 'auto' }}>
        <Box
          sx={{
            width: 200,
            borderRadius: 5,
            overflow: 'hidden',
            bgcolor: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.18)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.45)',
            backdropFilter: 'blur(6px)',
          }}
        >
          {feedRow('#a0b4ff')}
          {feedRow('#ff8fa3')}
          {feedRow('#8de3c0')}
        </Box>

        <FloatingBadge
          icon={<StorefrontIcon fontSize="small" color="primary" />}
          label={<T>Nuevo negocio cerca tuyo</T>}
          sx={{ top: -16, left: -84 }}
        />
        <FloatingBadge
          icon={<EventIcon fontSize="small" sx={{ color: '#c2185b' }} />}
          label={<T>Evento esta semana</T>}
          sx={{ bottom: 16, right: -92 }}
        />
        <FloatingBadge
          icon={<AccountBalanceIcon fontSize="small" sx={{ color: '#2e7d32' }} />}
          label={<T>Aviso oficial</T>}
          sx={{ top: 64, right: -78 }}
        />
      </Box>

      <Box textAlign="center">
        <Typography variant="h4" fontWeight={600} sx={{ mb: 2, lineHeight: 1.3 }}>
          <T>Conectá con</T>{' '}
          <Box component="span" sx={{ color: brandColors.accent }}><T>negocios</T></Box>,{' '}
          <Box component="span" sx={{ color: brandColors.accent }}><T>empresas</T></Box>{' '}
          <T>y</T>{' '}
          <Box component="span" sx={{ color: brandColors.accent }}><T>gobiernos</T></Box>{' '}
          <T>de tu comunidad</T>.
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.78 }}>
          <T>Enterate de novedades, eventos y oportunidades cerca tuyo, todo en un solo lugar.</T>
        </Typography>
      </Box>
    </Stack>
  </Box>
);

const AuthLayout = ({ children }) => (
  <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
    <AuthShowcase />
    <Box
      sx={{
        flex: { xs: '1 1 100%', md: '1 1 42%' },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto',
        minHeight: '100vh',
        width: '100%',
      }}
    >
      {children}
      {/* Link público (sin cuenta) a /explore: también es el camino por el
          que Google llega a las publicaciones desde la portada. */}
      <Button
        component={RouterLink}
        to="/explore"
        startIcon={<ExploreIcon />}
        sx={{ mt: 2, mb: 3, textTransform: 'none' }}
      >
        <T>Ver Notiline sin iniciar sesión</T>
      </Button>
      <PublicFooter sx={{ mt: 0, px: 2 }} />
    </Box>
  </Box>
);

export default AuthLayout;
